import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import styled from "styled-components";
import { useMutation } from "@apollo/client";
import { useToast } from "@songtradr-one/components-frontend";
import { graphql } from "../../../generated/gql";
import { errorText } from "../../../shared/statics/error-text";
import { staticText } from "../../../shared/statics/static-text";

// TEXT
export const text = staticText.appHeader.drawers.customerSupport;

// STYLES
export const StudioSupportLogo = styled.img`
  height: 8rem;
  width: 8rem;
`;

export const ButtonContainer = styled.div`
  margin-left: auto;
`;

// DATA
export const CustomerSupport_SubmitInquiry = graphql(`
  mutation CustomerSupportSubmitInquiry($inquiry: String!, $category: String!) {
    submitInquiry(inquiry: $inquiry, category: $category)
  }
`);

// TYPES
export type Props = {
  onOpenChange: (open: boolean) => void;
};

// CONSTANTS

// FUNCTIONS
export const INQUIRY_OPTIONS = Object.values(text.categories).map(
  (category) => ({ label: category, value: category }),
);

export const useCustomerSupport = ({ onOpenChange }: Props) => {
  const { addToast } = useToast();
  const [searchParams] = useSearchParams();
  const drawerState = searchParams.get("drawer");
  const [isOpen, setIsOpen] = useState(false);
  const [inquiry, setInquiry] = useState("");
  const [category, setCategory] = useState("");

  const [submitInquiry, { loading }] = useMutation(
    CustomerSupport_SubmitInquiry,
    {
      onCompleted: () => {
        addToast({
          title: text.confirmationTitle,
          text: text.confirmationBody,
          variant: "success",
        });
        handleOpenChange(false);
      },
      onError: (error) => {
        addToast({
          text: error.message || errorText.default.message,
          variant: "error",
        });
        handleOpenChange(false);
      },
    },
  );

  // Sync internal state with URL state
  useEffect(() => {
    setIsOpen(drawerState === "customer-support");
  }, [drawerState]);

  useEffect(() => {
    if (!isOpen) {
      setInquiry("");
      setCategory("");
    }
  }, [isOpen]);

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      // Start closing animation
      setIsOpen(false);
      // Delay URL update to allow animation to complete
      setTimeout(() => {
        onOpenChange(false);
      }, 300); // must be longer than the slideOut animation duration
    } else {
      onOpenChange(open);
    }
  };

  const handleSubmit = async () => {
    await submitInquiry({
      variables: {
        inquiry,
        category,
      },
    });
  };

  return {
    isOpen,
    inquiry,
    setInquiry,
    category,
    setCategory,
    loading,
    handleOpenChange,
    handleSubmit,
  };
};

// ASSETS
